import {AppState} from '../app.state';
import {createSelector} from '@ngrx/store';
import {ProductFeature} from '../../models/product.model';
import {SupportedBillingCountry} from "../../models/users.models";
import {TryTerraSupportedHealthProvider} from "../../models/try-terra.models";

export const selectUserState = (state: AppState) => state.user;

export const selectUserData = createSelector(
    selectUserState,
    (state) => state.data
);

export const selectUserError = createSelector(
    selectUserState,
    (state) => state.error
);
export const selectUserIsLoading = createSelector(
    selectUserState,
    (state) => state.isLoading
);

export const selectUserOpenOrders = createSelector(
    selectUserData,
    (user) => user?.openOrders ?? []
);

export const selectUserBillingInfoIsValid = createSelector(
    selectUserData,
    (user) => user?.fiscalCode != null && user?.fiscalCode.trim() !== '' &&
        user?.billingCity != null && user?.billingCity.trim() !== '' &&
        user?.billingStreet != null && user?.billingStreet.trim() !== '' &&
        user?.billingPostalCode != null && user?.billingPostalCode.trim() !== '' &&
        (user?.billingCountry === SupportedBillingCountry.Altro ||
            (user?.billingCountry != null && user?.billingCountry.trim() !== '' &&
                user?.billingProvince != null && user?.billingProvince.trim() !== ''))
);

export const selectUserHasPendingOrders = createSelector(
    selectUserOpenOrders,
    (orders) => orders.length > 0
);

export const selectUserFirstPendingOrder = createSelector(
    selectUserOpenOrders,
    (orders) => (orders.length > 0 ? orders[0] : null)
);

export const selectUserPendingOrder = (id: string) =>
    createSelector(selectUserOpenOrders, (orders) =>
        orders.find((o) => o.id === id)
    );

export const selectUserActivatedOrders = createSelector(
    selectUserData,
    (user) => user?.activatedOrders ?? []
);

export const selectUserFeatures = createSelector(
    selectUserData,
    (user) => user?.enabledFeatures
);

export const selectUserHasFeature = (feature: ProductFeature) =>
    createSelector(
        selectUserFeatures,
        (enabledFeatures) => enabledFeatures?.includes(feature) ?? false
    );

export const selectUserSubscriptions = createSelector(
    selectUserActivatedOrders,
    (activatedOrders) =>
        activatedOrders?.filter(
            (order) =>
                order.subscriptions != null && order.subscriptions.length > 0
        ) ?? []
);

export const selectUserHasActiveSubscriptions = createSelector(
    selectUserActivatedOrders,
    (activatedOrders) =>
        activatedOrders.some(order => order.subscriptions.length > 0 && order.activatedStatusReason === 'Active')
);

export const selectUserActiveSubscriptions = createSelector(
    selectUserSubscriptions,
    (subscriptions) =>
        subscriptions?.filter(
            (order) =>
                order.subscriptions != null &&
                order.subscriptions.length > 0 &&
                order.subscriptions.some(s => s.status === 'Active')
        ) ?? []
);

export const selectUserOneShotOrders = createSelector(
    selectUserActivatedOrders,
    (activatedOrders) =>
        activatedOrders?.filter(
            (order) =>
                !(order.orderItems ?? []).some((item) => item.product.family !== 'One shot purchase')
        ) ?? []
);

export const selectUserSubscriptionByProductCode = (productCode: string) =>
    createSelector(
        selectUserSubscriptions,
        (subscriptions) =>
            subscriptions?.find((order) =>
                order.orderItems.some(
                    (item) => item.product.productCode === productCode
                )
            ) ?? null
    );

export const selectUserHasLoginProvider = createSelector(
    selectUserData,
    (user) => user?.loginProvider != null
);

export const selectUserAgoraState = createSelector(
    selectUserState,
    (user) => user?.agora
);

export const selectUserAgoraChatState = createSelector(
    selectUserAgoraState,
    (agoraState) => agoraState?.chat
);

export const selectUserAgoraChatIsLogging = createSelector(
    selectUserAgoraChatState,
    (agoraChatState) => agoraChatState?.isLogging
);

export const selectUserAgoraChatIsLogged = createSelector(
    selectUserAgoraChatState,
    (agoraChatState) => agoraChatState?.isLogged
);

export const selectUserAgoraChatError = createSelector(
    selectUserAgoraChatState,
    (agoraChatState) => agoraChatState?.loginError
);

export const selectUserAgoraListConversationIsLoading = createSelector(
    selectUserAgoraChatState,
    (agoraChatState) => agoraChatState?.conversationListIsLoading
);

export const selectUserAgoraConversationList = createSelector(
    selectUserAgoraChatState,
    (agoraChatState) => agoraChatState?.conversationList
);

export const selectUserAgoraListConversationError = createSelector(
    selectUserAgoraChatState,
    (agoraChatState) => agoraChatState?.conversationListError
);

export const selectUserHasUnreadMessages = createSelector(
    selectUserAgoraConversationList,
    (agoraConversationList) => agoraConversationList?.some(conversation => (conversation.unreadNum ?? 0) > 0)
);

export const selectUserAgoraVideoCallState = createSelector(
    selectUserAgoraState,
    (agoraState) => agoraState?.videoCall
);

export const selectUserAgoraVideoCallIsActive = createSelector(
    selectUserAgoraVideoCallState,
    (agoraVideoCallState) => agoraVideoCallState?.isActive
);

export const selectUserAgoraVideoCallIsJoining = createSelector(
    selectUserAgoraVideoCallState,
    (agoraVideoCallState) => agoraVideoCallState?.isJoining
);

export const selectUserAgoraVideoCallIsMinimized = createSelector(
    selectUserAgoraVideoCallState,
    (agoraVideoCallState) => agoraVideoCallState?.isMinimized
);

export const selectUserTryTerraState = createSelector(
    selectUserState,
    (user) => user?.tryTerra
);

export const selectUserTryTerraUserInfo = createSelector(
    selectUserTryTerraState,
    (healthDataState) => healthDataState?.userInfo
);

export const selectUserTryTerraIsGettingUserInfo = createSelector(
    selectUserTryTerraState,
    (healthDataState) => healthDataState?.isGettingUserInfo
);

export const selectUserTryTerraUserInfoError = createSelector(
    selectUserTryTerraState,
    (healthDataState) => healthDataState?.userInfoError
);

export const selectUserTryTerraNativeInitialized = createSelector(
    selectUserTryTerraState,
    (healthDataState) => healthDataState?.nativeInitialized
);

export const selectUserTryTerraIsNativeInitializing = createSelector(
    selectUserTryTerraState,
    (healthDataState) => healthDataState?.isNativeInitializing
);

export const selectUserTryTerraNativeInitializationError = createSelector(
    selectUserTryTerraState,
    (healthDataState) => healthDataState?.nativeInitializationError
);

export const selectUserTryTerraIsNativeTriggeringWebhook = createSelector(
    selectUserTryTerraState,
    (healthDataState) => healthDataState?.nativeIsTriggeringWebhook
);

export const selectUserTryTerraNativeTriggerWebhookError = createSelector(
    selectUserTryTerraState,
    (healthDataState) => healthDataState?.nativeTriggerWebhookError
);

export const selectUserTryTerraHasProviders = createSelector(
    selectUserTryTerraUserInfo,
    (userInfo) => userInfo != null &&
        userInfo.length > 0 &&
        Object.values(TryTerraSupportedHealthProvider)
            .some((provider) => userInfo.some((user) => user.provider === provider)));

export const selectUserTryTerraHasNativeAppleConnection = createSelector(
    selectUserTryTerraUserInfo,
    (userInfo) => userInfo?.some((user) =>
        user.provider === TryTerraSupportedHealthProvider.APPLE) ?? false
);

export const selectUserTryTerraHasNativeHealthConnectConnection = createSelector(
    selectUserTryTerraUserInfo,
    (userInfo) => userInfo?.some((user) =>
        user.provider === TryTerraSupportedHealthProvider.HEALTH_CONNECT) ?? false
);

export const selectUserFreeTrialEndDate = createSelector(
    selectUserData,
    (user) => user?.freeTrialEndDate
);

export const selectUserGoals = createSelector(
    selectUserData,
    (user) => user?.goals
);

export const selectUserFileShareState = createSelector(
    selectUserState,
    (user) => user?.fileShare
);

export const selectUserFileShareIsUploading = createSelector(
    selectUserFileShareState,
    (fileShareState) => fileShareState?.isUploading
);

export const selectUserFileShareIsDownloading = createSelector(
    selectUserFileShareState,
    (fileShareState) => fileShareState?.isDownloading
);

export const selectUserFileShareIsLoading = createSelector(
    selectUserFileShareState,
    (fileShareState) => fileShareState?.isLoading
);

export const selectUserFileShareList = createSelector(
    selectUserFileShareState,
    (fileShareState) => {
        const files = [...fileShareState?.files];
        files.sort((a, b) => (new Date(b.createdDate)).getTime() - (new Date(a.createdDate)).getTime());
        return files;
    }
);

export const selectUserUploadedFileCount = createSelector(
    selectUserFileShareList,
    (files) => files.filter(file => file.qualification == null).length
);

export const selectUserHasSubscriptionOfCategories = (categories: string[]) =>
    createSelector(
        selectUserSubscriptions,
        (subscriptions) =>
            subscriptions?.some((order) =>
                order.orderItems.some(
                    (item) => item.product.categories.some(
                        (category) => categories.includes(category)
                    )
                )
            ) ?? false
    );
